import {Kind} from 'graphql';
import {isRootQueryIncludeVariable} from 'src/internal/revamp/graphql/compositionFragments/rootQueryInclude';
import {findEntryPointQuery} from 'src/internal/revamp/graphql/documentUtils/entrypoint';
import {extractVariableNamesFromTopLevelField} from 'src/internal/revamp/graphql/documentUtils/field';
import {
  collectAllReferencedFragments,
  getFragmentDefinitions,
} from 'src/internal/revamp/graphql/documentUtils/fragment';
import {createQueryDocument} from 'src/internal/revamp/graphql/documentUtils/query';

import type {ApolloLink} from '@apollo/client';
import type {
  DocumentNode,
  FieldNode,
  FragmentDefinitionNode,
  OperationDefinitionNode,
  SelectionNode,
  VariableDefinitionNode,
} from 'graphql';
import type {TopLevelFieldsToSchemaId} from 'src/internal/revamp/config/types';

export const UNKNOWN_SCHEMA = 'UNKNOWN_SCHEMA';

export type QueryBySchema = {
  queryDoc: DocumentNode;
  schemaLink: ApolloLink;
  containsNonNullableFields: boolean;
  // Used in the composition split link to skip the subquery if all include variables are false
  includeVariableNames: string[];
};

type QueryBySchemaData = {
  fields: FieldNode[];
  variables?: VariableDefinitionNode[];
  fragmentNodes: ReadonlyArray<FragmentDefinitionNode>;
  link: ApolloLink;
  containsNonNullableFields: boolean;
};

export type SchemaId = string;
export type SchemaIdToApolloLink = Record<SchemaId, ApolloLink>;

function getFieldsBySchemaInitState(
  schemaIdToApolloLink: SchemaIdToApolloLink,
): Record<SchemaId, QueryBySchemaData> {
  return Object.entries(schemaIdToApolloLink).reduce(
    (acc, [schemaId, schemaLink]) => {
      acc[schemaId as SchemaId] = {
        fields: [],
        fragmentNodes: [],
        link: schemaLink,
        containsNonNullableFields: false,
      };

      return acc;
    },
    {} as Record<SchemaId, QueryBySchemaData>,
  );
}

export function splitQueryBySchema(
  document: DocumentNode,
  topLevelFieldNameToMetadata: TopLevelFieldsToSchemaId['queries'],
  schemaIdToApolloLink: SchemaIdToApolloLink,
  newQueryName: string,
): Array<QueryBySchema> {
  const fieldsBySchema = getFieldsBySchemaInitState(schemaIdToApolloLink);
  const fragmentsInRootOperation: Map<string, FragmentDefinitionNode> = new Map(
    getFragmentDefinitions(document).map((fragment) => [
      fragment.name.value,
      fragment,
    ]),
  );
  const queryDefinition = findEntryPointQuery(document);

  queryDefinition.selectionSet.selections.forEach(
    (selectionNode: SelectionNode) => {
      if (selectionNode.kind !== Kind.FIELD) {
        return;
      }
      // [2.2] Check schema mapping for top level field
      const topLevelFieldName = selectionNode.name.value;

      const topLevelFieldData = topLevelFieldNameToMetadata[topLevelFieldName];
      // [2.3] Create or add field to the list in the schema key
      if (typeof topLevelFieldData?.schemaId === 'undefined') {
        // [2.3.1] Add fields without a schema to a special unknown schema for error handling
        fieldsBySchema[UNKNOWN_SCHEMA].fields.push(selectionNode);
      } else {
        const fieldsBySchemaData = fieldsBySchema[topLevelFieldData.schemaId];
        fieldsBySchemaData.fields.push(selectionNode);
        // [2.3.4] Determine whether sub-query contains non-nullable fields
        if (fieldsBySchemaData.containsNonNullableFields === false) {
          fieldsBySchemaData.containsNonNullableFields =
            topLevelFieldData.nonNullable;
        }
      }
    },
  );

  // [3] Gather all variable names and fragment names from fields by schema and pull them from operation
  (Object.entries(fieldsBySchema) as [SchemaId, QueryBySchemaData][]).forEach(
    ([schemaKey, {fields}]) => {
      const groupedVariableNamesForFields = Array.from(
        new Set(fields.flatMap(extractVariableNamesFromTopLevelField)),
      );

      const variableDefinitionsForAllFields =
        getVariableDefinitionNodesFromOperationDefinition(
          queryDefinition,
          groupedVariableNamesForFields,
        );
      fieldsBySchema[schemaKey].variables = variableDefinitionsForAllFields;

      fieldsBySchema[schemaKey].fragmentNodes = collectAllReferencedFragments(
        fields,
        fragmentsInRootOperation,
      );
    },
  );

  // [4] Build split queries
  const queriesBySchema: Array<QueryBySchema> = [];

  Object.entries(fieldsBySchema).forEach(([schemaKey, queryBySchemaData]) => {
    // [4.1] Skip creating queries for schemas with no fields in the composition query
    if (queryBySchemaData.fields.length > 0) {
      const includeVariableNames =
        queryBySchemaData.variables
          ?.filter((variable) => isRootQueryIncludeVariable(variable.variable))
          .map((variable) => variable.variable.name.value) || [];

      queriesBySchema.push({
        queryDoc: constructSchemaSubQueryDoc(
          newQueryName,
          schemaKey,
          queryBySchemaData,
        ),
        schemaLink: queryBySchemaData.link,
        containsNonNullableFields: queryBySchemaData.containsNonNullableFields,
        includeVariableNames,
      });
    }
  });

  return queriesBySchema;
}

function schemaKeyToCamelCase(schemaKey: string): string {
  return schemaKey.charAt(0).toUpperCase() + schemaKey.slice(1);
}

function constructSchemaSubQueryDoc(
  newQueryName: string,
  schemaKey: string,
  queryBySchemaData: QueryBySchemaData,
): DocumentNode {
  const splitQueryName = `${newQueryName}${schemaKeyToCamelCase(
    schemaKey,
  )}SplitQuery`;

  return createQueryDocument(
    splitQueryName,
    queryBySchemaData.variables || [],
    queryBySchemaData.fields,
    queryBySchemaData.fragmentNodes,
  );
}

function getVariableDefinitionNodesFromOperationDefinition(
  operation: OperationDefinitionNode,
  variableNames: ReadonlyArray<string>,
) {
  if (typeof operation.variableDefinitions !== 'undefined') {
    return operation.variableDefinitions.filter((variableDefinition) =>
      variableNames.includes(variableDefinition.variable.name.value),
    );
  }

  return [];
}
